import { FC, HTMLAttributes, PropsWithChildren, Ref, useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { generateTrackingId, TrackingOptions } from '@frontend/call-intel-core';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { WeaveUpgradeIcon } from '@frontend/self-serve#svg';
import { RequestUpgradeModal } from '@frontend/self-upgrade';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Button, Chip, Text, useModalControl } from '@frontend/design-system';
import { TRACKING_BASE } from '../../constants';

type TeaserCardProps = FC<PropsWithChildren<{ innerRef?: Ref<HTMLDivElement> }>> & HTMLAttributes<HTMLDivElement>;

interface TeaserCardNamespace extends TeaserCardProps {
  Header: typeof Header;
  Body: typeof Body;
  Description: typeof Description;
  FeatureChips: typeof FeatureChips;
  UpgradeAction: typeof UpgradeAction;
  ActionBar: typeof ActionBar;
}

// ── Root Container ──────────────────────────────────────────────────────────

export const TeaserCard: TeaserCardNamespace = ({ children, innerRef, ...props }) => {
  return (
    <div ref={innerRef} css={styles.container} {...props}>
      {children}
    </div>
  );
};

// ── Header ──────────────────────────────────────────────────────────────────

const Header: FC<HTMLAttributes<HTMLDivElement> & { showIcon?: boolean }> = ({ showIcon = true, ...props }) => {
  const { t } = useTranslation('callIntelInsights');

  return (
    <div css={styles.header} {...props}>
      {showIcon && <WeaveUpgradeIcon css={styles.upgradeIcon} />}
      <Text size='large' weight='bold'>
        {t('Free Trial of Call Intelligence')}
      </Text>
    </div>
  );
};

// —— Body ────────────────────────────────────────────────────────────────────

const Body: FC<HTMLAttributes<HTMLDivElement>> = ({ children, ...props }) => {
  return (
    <div css={styles.body} {...props}>
      {children}
    </div>
  );
};

// ── Description ─────────────────────────────────────────────────────────────

const Description: FC<{ text?: string }> = ({ text }) => {
  const { t } = useTranslation('callIntelInsights');

  return (
    <Text size='medium' weight='bold' color='default'>
      {text || t('Skip digging through calls. Be ready for your next conversation with this patient.')}
    </Text>
  );
};

// ── Feature Chips ───────────────────────────────────────────────────────────

const FEATURE_LABELS = [
  i18next.t('See call summaries', { ns: 'callIntelInsights' }),
  i18next.t('Know how the patient felt', { ns: 'callIntelInsights' }),
  i18next.t('Get follow-up tasks', { ns: 'callIntelInsights' }),
  i18next.t('Catch trends over time', { ns: 'callIntelInsights' }),
] as const;

const FeatureChips: FC = () => {
  return (
    <div css={styles.chipsContainer}>
      {FEATURE_LABELS.map((label) => (
        <Chip
          key={label}
          variant='seaweed'
          css={styles.featureChip}
          leftElement={<Icon name='check-small' css={styles.checkIcon} aria-label='check' />}
        >
          {label}
        </Chip>
      ))}
    </div>
  );
};

// ── Actions ──────────────────────────────────────────────────────────────

const UpgradeAction: FC<{ locationId?: string; trackingOptions?: TrackingOptions }> = ({
  locationId,
  trackingOptions,
}) => {
  const { t } = useTranslation('callIntelInsights');
  const { navigate } = useSettingsNavigate();
  const {
    modalProps: requestUpgradeModalProps,
    openModal: openRequestUpgradeModal,
    closeModal: closeRequestUpgradeModal,
  } = useModalControl();

  const selectedLocationIds = useMemo(() => (locationId ? [locationId] : []), [locationId]);
  const hasUpgradeAccess = useMemo(() => {
    return locationId ? hasSchemaACL(locationId, Permission.MANAGE_ADD_ON_SUBSCRIPTIONS) : false;
  }, [locationId]);

  const handleStartTrial = useCallback(() => {
    navigate({
      to: '/subscriptions/feature/:id',
      params: { id: 'call-intelligence' },
    });
  }, [navigate]);

  return (
    <>
      {hasUpgradeAccess ? (
        <Button
          variant='primary'
          size='small'
          onClick={handleStartTrial}
          trackingId={generateTrackingId({
            ...TRACKING_BASE,
            ...trackingOptions,
            context: 'start-free-trial',
          })}
        >
          {t('Start Free Trial')}
        </Button>
      ) : (
        <Button
          variant='primary'
          size='small'
          onClick={openRequestUpgradeModal}
          trackingId={generateTrackingId({
            ...TRACKING_BASE,
            ...trackingOptions,
            context: 'request-upgrade',
          })}
        >
          {t('Request Upgrade')}
        </Button>
      )}
      <RequestUpgradeModal
        feature={Feature.CALL_INTELLIGENCE}
        selectedLocationIds={selectedLocationIds}
        title={t('Request Call Intelligence')}
        description={t(
          "Add a note to your admin about why you want access to Call Intelligence. We'll send an email to them to start a free trial!"
        )}
        onActionSuccess={closeRequestUpgradeModal}
        {...requestUpgradeModalProps}
      />
    </>
  );
};
interface ActionBarProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'overlay';
  locationId?: string;
  onViewDemo?: () => void;
  onDismiss?: () => void;
  trackingOptions?: TrackingOptions;
}

const ActionBar: FC<ActionBarProps> = ({
  variant = 'default',
  locationId,
  onViewDemo,
  onDismiss,
  trackingOptions,
  ...rest
}) => {
  const { t } = useTranslation('callIntelInsights');

  return (
    <div css={[styles.actionBarBase, variant === 'overlay' && styles.actionBarOverlay]} {...rest}>
      <UpgradeAction locationId={locationId} trackingOptions={trackingOptions} />

      <Button
        variant='secondary'
        size='small'
        onClick={onViewDemo}
        data-testid='patient-insight-teaser-view-demo-button'
        trackingId={generateTrackingId({
          ...TRACKING_BASE,
          ...trackingOptions,
          context: 'view-demo',
        })}
      >
        {t('View Demo')}
      </Button>
      <Button
        variant='tertiary'
        size='small'
        onClick={onDismiss}
        data-testid='patient-insight-teaser-dismiss-button'
        trackingId={generateTrackingId({
          ...TRACKING_BASE,
          ...trackingOptions,
          context: 'dismiss',
        })}
        css={{ color: theme.colors.neutral50 }}
      >
        {t('Dismiss')}
      </Button>
    </div>
  );
};

// ── Attach sub-components ───────────────────────────────────────────────────

TeaserCard.Header = Header;
TeaserCard.Body = Body;
TeaserCard.Description = Description;
TeaserCard.FeatureChips = FeatureChips;
TeaserCard.ActionBar = ActionBar;
TeaserCard.UpgradeAction = UpgradeAction;

// ── Styles ──────────────────────────────────────────────────────────────────

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    border-radius: ${theme.borderRadius.medium};
    background-color: ${theme.colors.white};
    border: 1px solid ${theme.colors.secondary.seaweed50};
    overflow: hidden;
  `,
  header: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    background-color: ${theme.colorsV2.seaweed50(10)};
    padding: ${theme.spacing(1.5, 2)};
  `,
  body: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(2)};
  `,
  upgradeIcon: css`
    width: 24px;
    height: 24px;
    scale: 1.35; // adjust scale as image has extra padding
    position: relative;
    top: 2px;
  `,
  chipsContainer: css`
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(0.5)};
  `,
  featureChip: css`
    background: ${theme.colorsV2.seaweed50(10)};
    color: ${theme.colors.neutral90};
    font-size: ${theme.font.size.xs};
    max-width: 100%;
    cursor: default;
    gap: ${theme.spacing(1)};

    svg {
      color: ${theme.colors.secondary.seaweed60};
    }
  `,
  checkIcon: css`
    color: ${theme.colors.secondary.seaweed50};
    font-weight: bold;
    font-size: ${theme.font.size.xs};
    line-height: 1;
  `,
  actionBarBase: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    padding: ${theme.spacing(0, 2, 2, 2)};
  `,
  actionBarOverlay: css`
    padding: ${theme.spacing(1)};
    box-shadow: ${theme.shadows.floating};
    border-radius: ${theme.borderRadius.medium};
    background-color: ${theme.colors.white};
  `,
};
